/// <reference path="./env.d.ts" />

import type { LogOptions } from "./console-logging"
import { addMonitorBreadcrumb } from "./monitor-breadcrumb"
import { initSentryClient } from "./sentry-init"
import type { SentryConfig } from "./types"

export type {
  MonitorBootstrapConfig,
  SentryConfig,
  SeverityLevel,
} from "./types"
export { trackJourney } from "./business-metrics"
export type { JourneyOutcome } from "./business-metrics"
export {
  getMonitorPlatform,
  setMonitorPlatform,
  setMonitorUser,
  trackRoute,
} from "./monitor-context"
export { instrumentHttpClient } from "./http-instrumentation"
export { withMonitorSpan } from "./monitor-span"
export type { MonitorSpanOptions } from "./monitor-span"
export { startHealthMonitor } from "./health-monitor"
export { RouteTracker } from "./route-tracker"
export {
  captureException,
  captureMessage,
  flushMonitor,
  getSentry,
  isSentryEnabled,
  withScope,
} from "./sentry-state"
export { ErrorBoundary } from "./error-boundary"
export type { ErrorBoundaryProps } from "./error-boundary"

export function log(options: LogOptions) {
  addMonitorBreadcrumb(options)
}

export async function initSentry(config: SentryConfig) {
  await initSentryClient(config)

  if (config.eruda && typeof window !== "undefined" && import.meta.env.DEV) {
    try {
      const eruda = await import("eruda")
      eruda.default.init()
    } catch (error) {
      console.warn("Eruda not available:", error)
    }
  }
}

export { addMonitorBreadcrumb } from "./monitor-breadcrumb"
export {
  invokeBackendCall,
  recordBackendError,
  recordBackendResponse,
} from "./backend-trace"
export type { BackendCallRecord } from "./backend-trace"
export { serializeError, normalizeError, formatFlowErrorMessage } from "./error-serializer"
export {
  recordFlowOutcome,
  recordFlowStep,
  recordTrackedFailure,
  runFlowSpan,
  runInstrumentedCall,
} from "./flow-trace"
export type {
  FlowStepOptions,
  InstrumentedCallOptions,
  TrackedFailureOptions,
} from "./flow-trace"
export { scrubUrl, scrubValue } from "./sentry-privacy"

export function addBreadcrumb(options: LogOptions) {
  addMonitorBreadcrumb(options)
}

export function createMonitorConfig(
  release: string,
  platform: "appview" | "zalo"
) {
  const environment =
    import.meta.env.VITE_ENV ?? import.meta.env.MODE ?? "development"
  const isProductionEnv = environment === "prod" || environment === "production"

  return {
    enabled: true,
    platform,
    config: {
      dsn: import.meta.env.VITE_SENTRY_DSN ?? "",
      environment,
      release,
      tracesSampleRate: isProductionEnv ? 0.05 : 1,
      sampleRate: 1,
      eruda: import.meta.env.DEV,
      replay: {
        enabled: false,
        replaysOnErrorSampleRate: 0,
        replaysSessionSampleRate: 0,
      },
    },
  }
}
